import React, { useState, useEffect, useMemo } from "react";
import {
  Form,
  DatePicker,
  Select,
  Input,
  Modal,
  message,
  TimePicker,
  Button,
} from "antd";
import {
  LeaveDto,
  LeaveType,
  LeaveStatus,
  LeaveAdminFullUpdateDTO,
} from "../../../types/leaves.types";
import dayjs from "dayjs";
import FileUpload from "../components/upload/FileUpload";
import { FileType } from "../../../types/file.types";
import FileService from "../../../services/file/file.service";
import { handleApiError } from "../../../utils/errorHandler";
import { useTranslation } from "react-i18next";
import i18n from "../../../i18n/config";
import datePickerTr from "antd/lib/date-picker/locale/tr_TR";
import datePickerEn from "antd/lib/date-picker/locale/en_US";
import { EditOutlined } from "@ant-design/icons";
import { useSelector } from "react-redux";
import { RootState } from "../../../store/store";
import { PermissionType } from "../../../types/user.types";
import leaveService from "../../../services/leave/leave.service";

const { RangePicker } = DatePicker;
const { TextArea } = Input;

interface AdminLeaveEditModalProps {
  leaveData: LeaveDto | null;
  open: boolean;
  onCancel: () => void;
  onUpdate: (id: number, data: LeaveAdminFullUpdateDTO) => Promise<void>;
}

const AdminLeaveEditModal: React.FC<AdminLeaveEditModalProps> = ({
  leaveData,
  open,
  onCancel,
  onUpdate,
}) => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [selectedLeaveType, setSelectedLeaveType] = useState<LeaveType | null>(
    null
  );
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [leaveBalance, setLeaveBalance] = useState<Record<string, any>>({});
  const [balanceLoading, setBalanceLoading] = useState(false);
  const { user } = useSelector((state: RootState) => state.auth);
  const leaveBalanceEntries = useMemo(
    () => Object.entries(leaveBalance || {}),
    [leaveBalance]
  );
  // dayjs'in locale'ini güncelle
  useEffect(() => {
    dayjs.locale(i18n.language);
  }, [i18n.language]);

  // DatePicker'ın iç locale'i
  const pickerLocale = useMemo(() => {
    return i18n.language === "tr" ? datePickerTr : datePickerEn;
  }, [i18n.language]);

  useEffect(() => {
    if (open && leaveData) {
      form.setFieldsValue({
        type: leaveData.type,
        startDate: leaveData.startDate ? dayjs(leaveData.startDate) : null,
        endDate: leaveData.endDate ? dayjs(leaveData.endDate) : null,
        startTime: leaveData.startTime
          ? dayjs(leaveData.startTime, "HH:mm")
          : null,
        endTime: leaveData.endTime ? dayjs(leaveData.endTime, "HH:mm") : null,

        dateRange:
          leaveData.type === "hourly"
            ? undefined
            : [dayjs(leaveData.startDate), dayjs(leaveData.endDate)],
        date:
          leaveData.type === "hourly" ? dayjs(leaveData.startDate) : undefined,
        timeRange:
          leaveData.type === "hourly"
            ? [
                dayjs(leaveData.startTime, "HH:mm"),
                dayjs(leaveData.endTime, "HH:mm"),
              ]
            : undefined,
      });
      setSelectedLeaveType(leaveData.type);
    } else if (open) {
      form.resetFields();
      setSelectedLeaveType(null);
    }
  }, [open, leaveData, form]);

  useEffect(() => {
    const fetchLeaveBalance = async () => {
      if (!open || !leaveData?.user?.id) return;
      try {
        setBalanceLoading(true);
        const response = await leaveService.getComprehensiveLeaveBalance(leaveData.user.id);
        const balance = response?.leaveBalance || response?.data?.leaveBalance || {};
        setLeaveBalance(balance);
      } catch {
        setLeaveBalance({});
      } finally {
        setBalanceLoading(false);
      }
    };
    fetchLeaveBalance();
  }, [open, leaveData?.user?.id]);

  const handleUpdate = async () => {
    if (!leaveData) return;

    try {
      setLoading(true);
      const values = await form.validateFields();

      // Dosya yükleme (eğer varsa)
      let documentUrl = leaveData.documentUrl; // Mevcut dosyayı koru
      if (selectedFile) {
        try {
          const fileType = FileType.LEAVE;
          const uploadResponse = await FileService.getInstance().uploadFile(
            selectedFile,
            fileType
          );
          if (uploadResponse.filePath.path && uploadResponse.filePath) {
            documentUrl = uploadResponse.filePath.path;
          } else {
            message.error(t("common.error.fileUploadError"));
            setLoading(false);
            return;
          }
        } catch (error: any) {
          handleApiError(error, t);
          setLoading(false);
          return;
        }
      }

      let startDate, endDate;
      let [startTime, endTime] =
        values.type === "hourly" ? values.timeRange : [undefined, undefined];

      if (values.type === "hourly") {
        const date = values.date;
        startDate = date
          .hour(startTime.hour())
          .minute(startTime.minute())
          .second(0)
          .format("YYYY-MM-DD HH:mm:ss");

        endDate = date
          .hour(endTime.hour())
          .minute(endTime.minute())
          .second(0)
          .format("YYYY-MM-DD HH:mm:ss");

        startTime = startTime.format("HH:mm");
        endTime = endTime.format("HH:mm");
      } else if (values.type === "casual") {
        // Casual (mazeret) izni için tek tarih seçilir
        startDate = values.dateRange;
        endDate = values.dateRange;
        // YYYY-MM-DD formatı kullanarak timezone kaymasını önlüyoruz
        startDate = startDate.format("YYYY-MM-DD");
        endDate = endDate.format("YYYY-MM-DD");
      } else {
        // Normal izinler için tarih aralığı
        [startDate, endDate] = values.dateRange;
        // toISOString() UTC'ye çevirdiği için 1 gün geri kayma oluşuyor
        // YYYY-MM-DD formatı kullanarak bu sorunu çözüyoruz
        startDate = startDate.format("YYYY-MM-DD");
        endDate = endDate.format("YYYY-MM-DD");
      }

      const updateData: LeaveAdminFullUpdateDTO = {
        type: values.type,
        startDate,
        endDate,
        startTime: values.type === "hourly" ? startTime : undefined,
        endTime: values.type === "hourly" ? endTime : undefined,
        documentUrl: documentUrl,
      };

      await onUpdate(leaveData.id, updateData);
      message.success("İzin talebi güncellendi");
      form.resetFields();
      setSelectedFile(null);
      onCancel();
    } catch (error: any) {
      handleApiError(error, t);
    } finally {
      setLoading(false);
    }
  };

  const handleModalCancel = () => {
    form.resetFields();
    onCancel();
  };

  const handleLeaveTypeChange = (value: LeaveType) => {
    setSelectedLeaveType(value);
    form.setFieldsValue({
      dateRange: undefined,
      date: undefined,
      timeRange: undefined,
    });
  };

  const handleFileChange = (file: File | null) => {
    setSelectedFile(file);
  };

  return (
    <Modal
      title={`İzin Talebini Düzenle - ${leaveData?.user?.firstName} ${leaveData?.user?.lastName}`}
      open={open}
      onCancel={handleModalCancel}
      maskClosable={true}
      footer={[
        <Button
          key="cancel"
          type="default"
          onClick={handleModalCancel}
          disabled={loading}
        >
          Vazgeç
        </Button>,
        <Button
          key="submit"
          type="primary"
          icon={<EditOutlined />}
          onClick={handleUpdate}
          loading={loading}
          disabled={!user?.permissions?.includes(PermissionType.EDIT_LEAVE)}
        >
          Güncelle
        </Button>,
      ]}
      width={700}
      destroyOnClose
    >
      <Form form={form} layout="vertical">
        <Form.Item
          name="type"
          label="İzin Türü"
          rules={[{ required: true, message: "İzin türü seçiniz" }]}
        >
          <Select
            placeholder="İzin türünü seçiniz"
            onChange={handleLeaveTypeChange}
          >
            {Object.entries(LeaveType).map(([value, label]) => (
              <Select.Option key={value} value={label}>
                {t(`common.leaveType.${label}`)}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>

        {selectedLeaveType && (
          <div
            className="mb-3 p-2 rounded"
            style={{ background: "#f8f9fa", border: "1px solid #e9ecef" }}
          >
            {balanceLoading ? (
              <small className="text-muted">İzin hakkı bilgisi yükleniyor...</small>
            ) : leaveBalance?.[selectedLeaveType] ? (
              <div>
                <small className="text-muted d-block mb-2">
                  Bu izin türünde Kullanılan:{" "}
                  <strong>{leaveBalance[selectedLeaveType].used ?? 0}</strong>{" "}
                  {leaveBalance[selectedLeaveType].unit || "gün"} | Kalan:{" "}
                  <strong>{leaveBalance[selectedLeaveType].remaining ?? 0}</strong>{" "}
                  {leaveBalance[selectedLeaveType].unit || "gün"}
                  {leaveBalance[selectedLeaveType].total != null && (
                    <>
                      {" "}
                      | Toplam:{" "}
                      <strong>{leaveBalance[selectedLeaveType].total}</strong>{" "}
                      {leaveBalance[selectedLeaveType].unit || "gün"}
                    </>
                  )}
                </small>
                {leaveBalanceEntries.length > 0 && (
                  <div className="d-flex flex-wrap" style={{ gap: "4px" }}>
                    {leaveBalanceEntries.map(([type, value]) => (
                      <small
                        key={type}
                        className={`rounded ${
                          type === selectedLeaveType
                            ? "bg-primary text-white"
                            : "text-muted border bg-white"
                        }`}
                        style={{
                          fontSize: "10px",
                          lineHeight: 1.1,
                          padding: "3px 6px",
                          fontWeight: 400,
                        }}
                      >
                        {value?.typeName || t(`leaveTypes.${type}`)}:{" "}
                        {value?.used ?? 0}/{value?.remaining ?? 0}{" "}
                        {value?.unit || "gün"}
                      </small>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <small className="text-muted">Bu izin türü için bakiye bilgisi bulunamadı.</small>
            )}
          </div>
        )}

        {selectedLeaveType === "hourly" ? (
          <>
            <Form.Item
              name="date"
              label="Tarih"
              rules={[{ required: true, message: "Tarih seçiniz" }]}
            >
              <DatePicker
                className="w-100"
                format="DD.MM.YYYY"
                placeholder="Tarih seçiniz"
                locale={pickerLocale}
              />
            </Form.Item>

            <Form.Item
              name="timeRange"
              label="Saat Aralığı"
              rules={[{ required: true, message: "Saat aralığı seçiniz" }]}
            >
              <TimePicker.RangePicker
                className="w-100"
                format="HH:mm"
                minuteStep={30}
                locale={pickerLocale}
              />
            </Form.Item>
          </>
        ) 
        // : selectedLeaveType === "casual" ? (
        //   <Form.Item
        //     name="dateRange"
        //     label="Tarih"
        //     rules={[{ required: true, message: "Tarih seçiniz" }]}
        //   >
        //     <DatePicker
        //       className="w-100"
        //       format="DD.MM.YYYY"
        //       placeholder="Tarih seçiniz"
        //       locale={pickerLocale}
        //     />
        //   </Form.Item>
        // ) 
        : (
          <Form.Item
            name="dateRange"
            label="Tarih Aralığı"
            rules={[{ required: true, message: "Tarih aralığı seçiniz" }]}
          >
            <RangePicker
              className="w-100"
              format="DD.MM.YYYY"
              placeholder={["Başlangıç Tarihi", "Bitiş Tarihi"]}
              locale={pickerLocale}
            />
          </Form.Item>
        )}

        {/* İzin sebebi sadece görüntüleme (düzenlenemez) */}
        {leaveData?.reason && (
          <div className="mb-3">
            <label className="form-label text-dark">İzin Sebebi</label>
            <div
              className="form-control bg-light"
              style={{
                minHeight: "100px",
                padding: "8px 12px",
                border: "1px solid #d9d9d9",
                borderRadius: "6px",
                whiteSpace: "pre-wrap",
              }}
            >
              {leaveData.reason}
            </div>
          </div>
        )}

        <Form.Item
          name="document"
          label="Belge"
          extra="PDF, JPG, JPEG, PNG dosyaları yükleyebilirsiniz"
        >
          <FileUpload
            fileType={FileType.LEAVE}
            onChange={handleFileChange}
            maxCount={1}
            buttonText="Belge Yükle"
            accept=".pdf,.jpg,.jpeg,.png"
            disabled={loading}
          />
        </Form.Item>

        {leaveData?.documentUrl && (
          <Form.Item label="Mevcut Belge">
            <a
              href="#"
              onClick={async (e) => {
                e.preventDefault();
                try {
                  await FileService.getInstance().viewFileAsBlob(leaveData.documentUrl!);
                } catch (error) {
                  message.error('Belge görüntülenirken hata oluştu');
                }
              }}
            >
              Belgeyi Görüntüle
            </a>
          </Form.Item>
        )}
      </Form>
    </Modal>
  );
};

export default AdminLeaveEditModal;
